import type { ClassSummary } from '@school-erp/types';
import { createClassSchema, createSectionSchema } from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  Notice,
  errorMessage,
  primaryButton,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t } from '../../i18n';

interface Stream {
  id: string;
  name: string;
}

export function ClassesTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const [className, setClassName] = useState('');
  const [streamName, setStreamName] = useState('');
  const [sectionNames, setSectionNames] = useState<Record<string, string>>({});
  const [fieldError, setFieldError] = useState<string | null>(null);

  const classes = useQuery({
    queryKey: ['classes', schoolId],
    queryFn: () => api<ClassSummary[]>(`/schools/${schoolId}/classes`),
  });
  const streams = useQuery({
    queryKey: ['streams', schoolId],
    queryFn: () => api<Stream[]>(`/schools/${schoolId}/streams`),
  });
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['classes', schoolId] }),
      queryClient.invalidateQueries({ queryKey: ['streams', schoolId] }),
    ]);
  const run = (fn: () => Promise<unknown>) =>
    useMutation({
      mutationFn: fn,
      onSuccess: () => {
        setMessage(null);
        void refresh();
      },
      onError: (e) => setMessage(errorMessage(e)),
    });

  const addClass = run(() =>
    api(`/schools/${schoolId}/classes`, {
      method: 'POST',
      body: createClassSchema.parse({ name: className }),
    }).then(() => setClassName('')),
  );
  const addStream = run(() =>
    api(`/schools/${schoolId}/streams`, { method: 'POST', body: { name: streamName } }).then(() =>
      setStreamName(''),
    ),
  );
  const delClass = useMutation({
    mutationFn: (id: string) => api(`/schools/${schoolId}/classes/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: (e) => setMessage(errorMessage(e)),
  });
  const setStream = useMutation({
    mutationFn: ({ id, streamId }: { id: string; streamId: string | null }) =>
      api(`/schools/${schoolId}/classes/${id}`, { method: 'PATCH', body: { streamId } }),
    onSuccess: refresh,
    onError: (e) => setMessage(errorMessage(e)),
  });
  const addSection = useMutation({
    mutationFn: ({ classId, name }: { classId: string; name: string }) =>
      api(`/schools/${schoolId}/classes/${classId}/sections`, {
        method: 'POST',
        body: createSectionSchema.parse({ name }),
      }),
    onSuccess: (_d, v) => {
      setSectionNames((s) => ({ ...s, [v.classId]: '' }));
      setMessage(null);
      void refresh();
    },
    onError: (e) => setMessage(errorMessage(e)),
  });
  const delSection = useMutation({
    mutationFn: ({ classId, id }: { classId: string; id: string }) =>
      api(`/schools/${schoolId}/classes/${classId}/sections/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: (e) => setMessage(errorMessage(e)),
  });

  const editable = can('classes.create', schoolId);

  return (
    <section aria-label={t('acad.tab.classes')} className="space-y-4">
      {message && <Notice kind="error">{message}</Notice>}
      {editable && (
        <div className="flex flex-wrap items-end gap-4">
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              if (!className.trim()) return setFieldError(t('acad.name'));
              setFieldError(null);
              addClass.mutate();
            }}
            className="flex items-end gap-2"
          >
            <TextField
              label={t('acad.addClass')}
              value={className}
              error={fieldError}
              onChange={(e) => setClassName(e.target.value)}
            />
            <button type="submit" className={primaryButton}>
              {t('acad.add')}
            </button>
          </form>
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              if (streamName.trim()) addStream.mutate();
            }}
            className="flex items-end gap-2"
          >
            <TextField
              label={t('acad.addStream')}
              value={streamName}
              onChange={(e) => setStreamName(e.target.value)}
            />
            <button type="submit" className={secondaryButton}>
              {t('acad.add')}
            </button>
          </form>
        </div>
      )}
      {classes.isPending && <p role="status">{t('common.loading')}</p>}
      {classes.data?.length === 0 && <p className="text-slate-600">{t('acad.noClasses')}</p>}
      <ul className="space-y-3">
        {classes.data?.map((c) => (
          <li key={c.id} className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-semibold">{c.name}</h3>
              <div className="flex items-center gap-2">
                <label className="text-sm">
                  <span className="sr-only">
                    {t('acad.stream')} — {c.name}
                  </span>
                  <select
                    value={c.streamId ?? ''}
                    disabled={!can('classes.update', schoolId)}
                    onChange={(e) =>
                      setStream.mutate({ id: c.id, streamId: e.target.value || null })
                    }
                    className={`${selectClass} mt-0`}
                  >
                    <option value="">{t('acad.noStream')}</option>
                    {streams.data?.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </label>
                {can('classes.delete', schoolId) && (
                  <button
                    type="button"
                    className="rounded border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                    onClick={() =>
                      window.confirm(t('common.confirmDelete', { name: c.name })) &&
                      delClass.mutate(c.id)
                    }
                  >
                    {t('acad.delete')}
                  </button>
                )}
              </div>
            </div>
            <p className="mt-2 text-sm text-slate-600">{t('acad.sections')}:</p>
            <ul className="mt-1 flex flex-wrap gap-2">
              {c.sections.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center gap-1 rounded bg-slate-100 px-2 py-1 text-sm"
                >
                  {s.name}
                  {can('classes.delete', schoolId) && (
                    <button
                      type="button"
                      aria-label={`${t('acad.delete')} ${c.name} ${s.name}`}
                      className="text-red-700"
                      onClick={() => delSection.mutate({ classId: c.id, id: s.id })}
                    >
                      ×
                    </button>
                  )}
                </li>
              ))}
            </ul>
            {editable && (
              <form
                noValidate
                onSubmit={(e) => {
                  e.preventDefault();
                  const name = sectionNames[c.id]?.trim();
                  if (name) addSection.mutate({ classId: c.id, name });
                }}
                className="mt-2 flex items-end gap-2"
              >
                <TextField
                  label={`${t('acad.addSection')} (${c.name})`}
                  value={sectionNames[c.id] ?? ''}
                  onChange={(e) => setSectionNames((s) => ({ ...s, [c.id]: e.target.value }))}
                />
                <button type="submit" className={secondaryButton}>
                  {t('acad.add')}
                </button>
              </form>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
